// Lê números soltos numa anotação escrita à mão.
//
// "às 8h a enfermeira mediu sat 96 e temp 37,4, dormiu bem" → duas medidas.
//
// Regra que atravessa tudo: **o texto é o registro principal.** Isto aqui é
// bônus. Se não achar nada, ou achar errado, a anotação se salva do mesmo jeito
// — e nada vira leitura sem alguém confirmar na tela.
//
// Vocabulário do quarto, não do livro: a enfermagem fala "dextro" e "HGT" para
// glicemia, "sat" para saturação, "tax" para temperatura axilar.

import { FAIXA_PLAUSIVEL, type TipoLeitura } from '@medicov1/shared';

export interface Achado {
  tipo: TipoLeitura;
  valor: number;
  /** Trecho do texto que originou o número, para a pessoa conferir. */
  trecho: string;
}

const num = String.raw`(\d{1,3}(?:[.,]\d{1,2})?)`;

/** Ordem importa: padrões mais específicos primeiro (PA antes de números soltos). */
const PADROES: { tipo: TipoLeitura; re: RegExp; segundo?: TipoLeitura }[] = [
  // Pressão: "120/80", "120 x 80", "pa 120 por 80"
  {
    tipo: 'pa_sistolica',
    segundo: 'pa_diastolica',
    re: new RegExp(String.raw`(?:pa|press[aã]o)?\s*(\d{2,3})\s*(?:\/|x|por)\s*(\d{2,3})`, 'i'),
  },
  { tipo: 'saturacao', re: new RegExp(String.raw`(?:sat(?:ura[çc][aã]o)?|spo2|o2)\s*(?:de|em|:)?\s*${num}`, 'i') },
  { tipo: 'temperatura', re: new RegExp(String.raw`(?:temp(?:eratura)?|tax|febre)\s*(?:de|em|:)?\s*${num}`, 'i') },
  { tipo: 'glicemia', re: new RegExp(String.raw`(?:glicemia|glicose|dextro|hgt)\s*(?:de|em|:)?\s*${num}`, 'i') },
  { tipo: 'frequencia_cardiaca', re: new RegExp(String.raw`(?:fc|pulso|batimentos?|bpm)\s*(?:de|em|:)?\s*${num}`, 'i') },
  { tipo: 'frequencia_respiratoria', re: new RegExp(String.raw`(?:fr|respira[çc][aã]o)\s*(?:de|em|:)?\s*${num}`, 'i') },
  { tipo: 'diurese', re: new RegExp(String.raw`(?:diurese|urina|urinou|xixi)\s*(?:de|em|:)?\s*${num}`, 'i') },
  { tipo: 'dieta_enteral', re: new RegExp(String.raw`(?:dieta|sonda)\s*(?:de|em|:)?\s*${num}`, 'i') },
  { tipo: 'peso', re: new RegExp(String.raw`(?:peso|pesou)\s*(?:de|em|:)?\s*${num}`, 'i') },
];

const paraNumero = (texto: string) => Number(texto.replace(',', '.'));

export function extrairDoTexto(texto: string): Achado[] {
  const achados: Achado[] = [];

  for (const padrao of PADROES) {
    const casou = padrao.re.exec(texto);
    if (!casou) continue;

    const valores: { tipo: TipoLeitura; valor: number }[] = [
      { tipo: padrao.tipo, valor: paraNumero(casou[1]!) },
    ];
    if (padrao.segundo && casou[2]) {
      valores.push({ tipo: padrao.segundo, valor: paraNumero(casou[2]) });
    }

    for (const { tipo, valor } of valores) {
      const faixa = FAIXA_PLAUSIVEL[tipo];
      // Fora da faixa possível provavelmente não era uma medida — é hora, dose,
      // número de leito. Melhor ignorar do que sugerir bobagem para confirmar.
      if (valor < faixa.min || valor > faixa.max) continue;
      if (achados.some((a) => a.tipo === tipo)) continue;
      achados.push({ tipo, valor, trecho: casou[0]!.trim() });
    }
  }

  return achados;
}

export const ROTULO_ACHADO: Record<TipoLeitura, string> = {
  glicemia: 'Glicemia',
  pa_sistolica: 'PA sistólica',
  pa_diastolica: 'PA diastólica',
  frequencia_cardiaca: 'Frequência cardíaca',
  saturacao: 'Saturação',
  temperatura: 'Temperatura',
  frequencia_respiratoria: 'Freq. respiratória',
  diurese: 'Diurese',
  evacuacoes: 'Evacuações',
  dieta_enteral: 'Dieta enteral',
  balanco_hidrico: 'Balanço hídrico',
  peso: 'Peso',
};
